import {
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, FormApi } from 'informed';
import { AuthContext, CommonContext, useApi } from '@repo-level/core-front';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash';

import { Button } from 'components/buttons';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { ModalDefaultProps } from 'components/Modal';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { useTheme } from 'hooks/theme';
import { AddonsContext } from 'contexts/addons';
import { formatWalletValue } from 'helpers/wallet';
import { maskNumber } from './helpers';
import { exchangeCompointsMoney } from 'api/common';
import { Select } from 'components/form/Select';
import {
  amountWithCurrencySymbolString,
  CurrencyCode,
  CurrencyLimit,
  currencyNameMap,
  decimalPlacesMapCrypto,
} from 'helpers/currency';
import { required } from 'helpers/validators';
import { showTooltip } from 'helpers/tooltip';
import { translationFactory } from 'helpers/trans';
import { TooltipTypes } from 'modules/globalTooltip';
import { Spinner } from 'components/Spinner';
import { useAnalytics } from 'helpers/analytics';
import { AnalyticsEventName } from 'api/analytics';
import { useCurrentOptionWallet } from 'hooks/wallet';

import { ScExchangeCompointsModal } from './styled';

interface ExchangeCompointsApi {
  compoints: number;
}

export const ExchangeCompointsModal = forwardRef<
  HTMLDivElement,
  ModalDefaultProps
>(({ onClose }, ref) => {
  const {
    t,
    i18n: { language },
  } = useTranslation('bonuses');
  const { isMobileS } = useContext(CommonUtilsContext);
  const { wallets, pendingUser } = useContext(AuthContext);
  const { currencies } = useContext(CommonContext);
  const {
    compointsRatesMoney,
    playerCompoints,
    pendingCompointsRatesMoney,
    pendingPlayerCompoints,
    fetchPlayerCompoints,
  } = useContext(AddonsContext);
  const { sendEventToAnalytics } = useAnalytics();

  const currentOptionWallet = useCurrentOptionWallet();

  const [
    requestExchangeCompointsMoney,
    { pending: pendingExchangeCompoints },
  ] = useApi(exchangeCompointsMoney);

  const formApiRef = useRef<FormApi<ExchangeCompointsApi>>();
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [compointsToExchange, setCompointsToExchange] = useState<number>(0);
  const { infoIcon: InfoIcon, equalitySign: EqualitySign } = useTheme();

  const [exchangeCurrency, setExchangeCurrency] = useState<string>();

  const currentCompointsCurrencyRate = useMemo(() => {
    if (compointsRatesMoney) {
      return compointsRatesMoney[0]?.rates.find(
        ({ currency }) => currency === exchangeCurrency
      );
    }
  }, [compointsRatesMoney, exchangeCurrency]);

  const modalSettings: ModalTemplateSettings = useMemo(() => {
    return isMobileS
      ? {
          type: 'mobile',
          mode: {
            type: 'default',
          },
        }
      : {
          type: 'desktop',
          size: 'small',
          mode: {
            type: 'default',
          },
        };
  }, [isMobileS]);

  const userCompoints = useMemo(
    () => playerCompoints?.chargeable?.points || 0,
    [playerCompoints?.chargeable?.points]
  );

  const minExchange = useMemo(
    () => (compointsRatesMoney ? compointsRatesMoney[0]?.rates[0].points : 100),
    [compointsRatesMoney]
  );

  const userCurrenciesSelectOptions = useMemo(() => {
    return wallets.map(({ currency }) => {
      const name = (() => {
        switch (currency) {
          case CurrencyCode.LTC:
            return 'mLTC';
          case CurrencyCode.DOG:
            return 'DOGE';
          case CurrencyCode.BTC:
            return 'mBTC';
          case CurrencyCode.BCH:
            return 'mBCH';
          case CurrencyCode.ETH:
            return 'mETH';
          default:
            return currency;
        }
      })();

      return {
        name: name,
        value: currency,
      };
    });
  }, [wallets]);

  const setWagerSumInputValue = useCallback(() => {
    const compointsSum = Number(formApiRef.current?.getValue('compoints') || 0);

    const validCompointsSum = compointsSum || 0;

    setCompointsToExchange(
      validCompointsSum > minExchange - 1 ? validCompointsSum : 0
    );

    if (validCompointsSum < minExchange) {
      return formApiRef.current?.setValue('wager', 0);
    }

    if (!currentCompointsCurrencyRate) return;

    const currencyRate = formatWalletValue(
      currentCompointsCurrencyRate.currency,
      currentCompointsCurrencyRate.amountCents,
      currencies
    );

    if (!isNumber(currencyRate)) return;

    const { currency } = currentCompointsCurrencyRate;

    const wagerSumValue = (Number(compointsSum) * currencyRate) / minExchange;

    const displayName =
      userCurrenciesSelectOptions.find(({ value }) => value === currency)
        ?.name || currency;

    if (wagerSumValue > CurrencyLimit[currency]) {
      const limitAmount = amountWithCurrencySymbolString(
        displayName,
        CurrencyLimit[currency]
      );

      formApiRef.current?.setError(
        'wager',
        t('validation:maxAmountError', {
          amount: limitAmount,
        })
      );
    } else {
      formApiRef.current?.setError('wager', undefined);
    }

    const decimals = decimalPlacesMapCrypto[currencyNameMap[currency]] || 2;
    formApiRef.current?.setValue('wager', wagerSumValue.toFixed(decimals));
  }, [
    currencies,
    currentCompointsCurrencyRate,
    minExchange,
    t,
    userCurrenciesSelectOptions,
  ]);

  useEffect(() => {
    setWagerSumInputValue();
  }, [setWagerSumInputValue, currentCompointsCurrencyRate]);

  useEffect(() => {
    setExchangeCurrency(currentOptionWallet?.currency);
  }, [currentOptionWallet?.currency]);

  const handleChangeCompointsField = useCallback(() => {
    if (!playerCompoints || !formApiRef.current)
      return setIsButtonDisabled(true);

    const hasCompointsSumError = !!formApiRef.current.getError('compoints');
    const hasWagerSumError = !!formApiRef.current.getError('wager');

    const compointsFieldValue = formApiRef.current.getValue('compoints');

    if (!compointsFieldValue) {
      formApiRef.current.setError('wager', undefined);
      formApiRef.current.setValue('wager', 0);
      setCompointsToExchange(0);
      return setIsButtonDisabled(true);
    }

    const compointsFieldValueNumber = isNumber(compointsFieldValue)
      ? compointsFieldValue
      : Number(compointsFieldValue);

    const disableButton =
      hasWagerSumError ||
      hasCompointsSumError ||
      playerCompoints.chargeable.points < compointsFieldValueNumber ||
      compointsFieldValueNumber < minExchange;

    setIsButtonDisabled(disableButton);

    setWagerSumInputValue();
  }, [
    minExchange,
    playerCompoints,
    setWagerSumInputValue,
    setIsButtonDisabled,
  ]);

  const handleSubmit = async (values: ExchangeCompointsApi) => {
    if (!compointsRatesMoney || !currentCompointsCurrencyRate) return;

    const { error } = await requestExchangeCompointsMoney({
      currency: currentCompointsCurrencyRate.currency,
      group: compointsRatesMoney[0].group,
      points: Number(values.compoints),
    }).request;

    if (error != null) {
      showTooltip({
        type: TooltipTypes.ERROR,
        translate: true,
        title: translationFactory('bonuses:sthWentWrong'),
      });

      sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__EXCHANGE_CP_MODAL__EXCHANGE__FAILURE,
      });

      return;
    }

    if (!error) {
      sendEventToAnalytics({
        name: AnalyticsEventName.PROMO__EXCHANGE_CP_MODAL__EXCHANGE__SUCCESS,
      });

      fetchPlayerCompoints();

      onClose && onClose();

      return;
    }
  };

  useEffect(() => {
    if (
      (!currentOptionWallet?.currency && !pendingUser) ||
      (!compointsRatesMoney && !pendingCompointsRatesMoney)
    ) {
      onClose && onClose();
    }
  }, [
    compointsRatesMoney,
    currentOptionWallet?.currency,
    onClose,
    pendingCompointsRatesMoney,
    pendingUser,
  ]);

  return pendingUser || pendingCompointsRatesMoney ? (
    <Spinner size={64} absoluteCenter />
  ) : (
    <ModalTemplate ref={ref} onClose={onClose} settings={modalSettings}>
      <ScExchangeCompointsModal>
        <div className="header">
          <div className="title">{t('exchangePoints.exchangeablePoints')}</div>
          <div className="balance">
            {t('exchangePoints.available', {
              amount: userCompoints,
            })}
          </div>
        </div>
        <div className="warning">
          <InfoIcon />
          <div>
            {t('exchangePoints.minExchange', {
              min: minExchange,
            })}
          </div>
        </div>
        <Form<ExchangeCompointsApi>
          onSubmit={handleSubmit}
          getApi={(api) => {
            formApiRef.current = api;
          }}
        >
          <div className="exchange-form">
            <div className="exhange-form-input">
              <div className={`header ${language.toLowerCase()}`}>
                <div className="title">{t('exchangePoints.rate')}</div>
                {currentCompointsCurrencyRate && (
                  <div className="value">
                    {': '}
                    {currentCompointsCurrencyRate.points} CP /{' '}
                    {formatWalletValue(
                      currentCompointsCurrencyRate.currency,
                      currentCompointsCurrencyRate.amountCents,
                      currencies,
                      true
                    )}
                  </div>
                )}
              </div>
              <FormGroup marginBottom={0}>
                <Input
                  autoComplete="off"
                  validateOnChange
                  field="compoints"
                  placeholder={'0'}
                  onChange={handleChangeCompointsField}
                  internalLabel={'CP'}
                  validate={required}
                  bold
                  mask={maskNumber}
                />
              </FormGroup>
            </div>

            <div className="eqality">
              <EqualitySign />
            </div>

            <div className="exhange-form-input">
              <div className={`header ${language.toLowerCase()}`}>
                <div className="title">{t('exchangePoints.wager')}</div>
                <div className="value">
                  {compointsRatesMoney && `: ${compointsRatesMoney[0].wager}`}
                </div>
              </div>
              <div className="wager-inputs">
                <FormGroup marginBottom={0}>
                  <Input
                    autoComplete="off"
                    validateOnBlur
                    field="wager"
                    disabled
                    initialValue={'0'}
                    bold
                  />
                </FormGroup>

                <Select
                  field="currency"
                  options={userCurrenciesSelectOptions}
                  initialValue={currentOptionWallet?.currency}
                  onSelect={(value: any) => setExchangeCurrency(value)}
                  withBorderMode
                  placeholder={t('currency')}
                  noSelectioinIfOneOption
                />
              </div>
            </div>
          </div>

          <div className="submit-btn">
            <Button
              green
              xLarge
              type="submit"
              pending={false}
              disabled={
                isButtonDisabled ||
                pendingCompointsRatesMoney ||
                pendingExchangeCompoints ||
                pendingPlayerCompoints
              }
            >
              {t('exchangePoints.exchange')} {compointsToExchange} CP
            </Button>
          </div>
        </Form>
      </ScExchangeCompointsModal>
    </ModalTemplate>
  );
});
